import React, { useState } from 'react';
import { ArrowUpRight, ArrowDownRight, Download, Filter, Search, ChevronLeft, ChevronRight } from 'lucide-react';
const TransactionHistory = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  // Mock data for demonstration
  const transactions = [{
    id: 1,
    type: 'credit',
    description: 'Wallet Funding',
    amount: 500,
    currency: 'USD',
    date: '2023-06-15',
    status: 'completed',
    reference: 'TRX123456789'
  }, {
    id: 2,
    type: 'debit',
    description: 'Netflix Subscription',
    amount: 15.99,
    currency: 'USD',
    date: '2023-06-14',
    status: 'completed',
    reference: 'TRX123456788'
  }, {
    id: 3,
    type: 'debit',
    description: 'Airtime Recharge',
    amount: 10,
    currency: 'USD',
    date: '2023-06-12',
    status: 'completed',
    reference: 'TRX123456787'
  }, {
    id: 4,
    type: 'credit',
    description: 'Refund',
    amount: 25.5,
    currency: 'USD',
    date: '2023-06-10',
    status: 'completed',
    reference: 'TRX123456786'
  }, {
    id: 5,
    type: 'debit',
    description: 'DSTV Subscription',
    amount: 45,
    currency: 'USD',
    date: '2023-06-08',
    status: 'completed',
    reference: 'TRX123456785'
  }, {
    id: 6,
    type: 'credit',
    description: 'Salary Payment',
    amount: 3000,
    currency: 'USD',
    date: '2023-06-05',
    status: 'completed',
    reference: 'TRX123456784'
  }, {
    id: 7,
    type: 'debit',
    description: 'Amazon Purchase',
    amount: 129.99,
    currency: 'USD',
    date: '2023-06-03',
    status: 'completed',
    reference: 'TRX123456783'
  }, {
    id: 8,
    type: 'debit',
    description: 'Electricity Bill',
    amount: 75.25,
    currency: 'USD',
    date: '2023-06-01',
    status: 'completed',
    reference: 'TRX123456782'
  }, {
    id: 9,
    type: 'credit',
    description: 'Bank Transfer',
    amount: 1000,
    currency: 'USD',
    date: '2023-05-28',
    status: 'completed',
    reference: 'TRX123456781'
  }, {
    id: 10,
    type: 'debit',
    description: 'Gym Membership',
    amount: 35,
    currency: 'USD',
    date: '2023-05-25',
    status: 'completed',
    reference: 'TRX123456780'
  }];
  // Filter transactions based on search term and filter type
  const filteredTransactions = transactions.filter(transaction => {
    const matchesSearch = transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) || transaction.reference.toLowerCase().includes(searchTerm.toLowerCase());
    if (filterType === 'all') return matchesSearch;
    return matchesSearch && transaction.type === filterType;
  });
  // Pagination
  const itemsPerPage = 5;
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const paginatedTransactions = filteredTransactions.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
  const getCurrencySymbol = (code: string) => {
    const symbols: {
      [key: string]: string;
    } = {
      USD: '$',
      GBP: '£',
      NGN: '₦',
      ZAR: 'R',
      GHS: '₵'
    };
    return symbols[code] || code;
  };
  return <div className="py-6" data-id="element-634">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8" data-id="element-635">
        <h1 className="text-2xl font-semibold text-gray-900" data-id="element-636">
          Transaction History
        </h1>
        <p className="mt-1 text-sm text-gray-500" data-id="element-637">
          View and manage all your transaction records
        </p>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 mt-6" data-id="element-638">
        <div className="bg-white shadow overflow-hidden sm:rounded-lg" data-id="element-639">
          <div className="px-4 py-5 sm:p-6" data-id="element-640">
            {/* Search and Filter Controls */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 space-y-4 md:space-y-0" data-id="element-641">
              <div className="max-w-lg w-full lg:max-w-xs" data-id="element-642">
                <label htmlFor="search" className="sr-only" data-id="element-643">
                  Search transactions
                </label>
                <div className="relative" data-id="element-644">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-645">
                    <Search className="h-5 w-5 text-gray-400" data-id="element-646" />
                  </div>
                  <input id="search" name="search" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search transactions" type="search" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-647" />
                </div>
              </div>
              <div className="flex space-x-2" data-id="element-648">
                <div className="relative inline-block text-left" data-id="element-649">
                  <div data-id="element-650">
                    <button type="button" className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-100 focus:ring-blue-500" id="filter-menu-button" data-id="element-651">
                      <Filter className="h-5 w-5 mr-2 text-gray-400" data-id="element-652" />
                      {filterType === 'all' ? 'All Transactions' : filterType === 'credit' ? 'Credits Only' : 'Debits Only'}
                    </button>
                  </div>
                  <div className="origin-top-right absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-10 hidden" data-id="element-653">
                    <div className="py-1" role="none" data-id="element-654">
                      <button onClick={() => setFilterType('all')} className="text-gray-700 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100" data-id="element-655">
                        All Transactions
                      </button>
                      <button onClick={() => setFilterType('credit')} className="text-gray-700 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100" data-id="element-656">
                        Credits Only
                      </button>
                      <button onClick={() => setFilterType('debit')} className="text-gray-700 block px-4 py-2 text-sm w-full text-left hover:bg-gray-100" data-id="element-657">
                        Debits Only
                      </button>
                    </div>
                  </div>
                </div>
                <button type="button" className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-658">
                  <Download className="h-5 w-5 mr-2 text-gray-400" data-id="element-659" />
                  Export
                </button>
              </div>
            </div>
            {/* Filter Pills */}
            <div className="flex space-x-2 mb-4" data-id="element-660">
              <button onClick={() => setFilterType('all')} className={`px-3 py-1 rounded-full text-sm font-medium ${filterType === 'all' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-661">
                All
              </button>
              <button onClick={() => setFilterType('credit')} className={`px-3 py-1 rounded-full text-sm font-medium ${filterType === 'credit' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-662">
                Credits
              </button>
              <button onClick={() => setFilterType('debit')} className={`px-3 py-1 rounded-full text-sm font-medium ${filterType === 'debit' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-663">
                Debits
              </button>
            </div>
            {/* Transaction Table */}
            <div className="flex flex-col" data-id="element-664">
              <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8" data-id="element-665">
                <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8" data-id="element-666">
                  <div className="overflow-hidden border-b border-gray-200 sm:rounded-lg" data-id="element-667">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-668">
                      <thead className="bg-gray-50" data-id="element-669">
                        <tr data-id="element-670">
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-671">
                            Transaction
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-672">
                            Date
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-673">
                            Amount
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-674">
                            Status
                          </th>
                          <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-675">
                            Reference
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-676">
                        {paginatedTransactions.length > 0 ? paginatedTransactions.map(transaction => <tr key={transaction.id} data-id="element-677">
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-678">
                                <div className="flex items-center" data-id="element-679">
                                  <div className={`flex-shrink-0 h-8 w-8 rounded-full flex items-center justify-center ${transaction.type === 'credit' ? 'bg-green-100' : 'bg-red-100'}`} data-id="element-680">
                                    {transaction.type === 'credit' ? <ArrowUpRight className={`h-4 w-4 text-green-600`} data-id="element-681" /> : <ArrowDownRight className={`h-4 w-4 text-red-600`} data-id="element-682" />}
                                  </div>
                                  <div className="ml-4" data-id="element-683">
                                    <div className="text-sm font-medium text-gray-900" data-id="element-684">
                                      {transaction.description}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-685">
                                <div className="text-sm text-gray-900" data-id="element-686">
                                  {transaction.date}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-687">
                                <div className={`text-sm font-medium ${transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}`} data-id="element-688">
                                  {transaction.type === 'credit' ? '+' : '-'}{' '}
                                  {getCurrencySymbol(transaction.currency)}
                                  {transaction.amount.toFixed(2)}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap" data-id="element-689">
                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800" data-id="element-690">
                                  {transaction.status}
                                </span>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-691">
                                {transaction.reference}
                              </td>
                            </tr>) : <tr data-id="element-692">
                            <td colSpan={5} className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center" data-id="element-693">
                              No transactions found
                            </td>
                          </tr>}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
            {/* Pagination */}
            {filteredTransactions.length > 0 && <div className="flex items-center justify-between mt-6" data-id="element-694">
                <div className="flex-1 flex justify-between sm:hidden" data-id="element-695">
                  <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === 1 ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-696">
                    Previous
                  </button>
                  <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${currentPage === totalPages ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-697">
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between" data-id="element-698">
                  <div data-id="element-699">
                    <p className="text-sm text-gray-700" data-id="element-700">
                      Showing{' '}
                      <span className="font-medium" data-id="element-701">
                        {(currentPage - 1) * itemsPerPage + 1}
                      </span>{' '}
                      to{' '}
                      <span className="font-medium" data-id="element-702">
                        {Math.min(currentPage * itemsPerPage, filteredTransactions.length)}
                      </span>{' '}
                      of{' '}
                      <span className="font-medium" data-id="element-703">
                        {filteredTransactions.length}
                      </span>{' '}
                      results
                    </p>
                  </div>
                  <div data-id="element-704">
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination" data-id="element-705">
                      <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${currentPage === 1 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-706">
                        <span className="sr-only" data-id="element-707">Previous</span>
                        <ChevronLeft className="h-5 w-5" data-id="element-708" />
                      </button>
                      {/* Page numbers */}
                      {[...Array(totalPages)].map((_, i) => <button key={i + 1} onClick={() => setCurrentPage(i + 1)} className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i + 1 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`} data-id="element-709">
                          {i + 1}
                        </button>)}
                      <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${currentPage === totalPages ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'}`} data-id="element-710">
                        <span className="sr-only" data-id="element-711">Next</span>
                        <ChevronRight className="h-5 w-5" data-id="element-712" />
                      </button>
                    </nav>
                  </div>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default TransactionHistory;